import type { TFunction } from "i18next";
import type { Writable } from "type-fest";
import { v4 as uuidv4 } from "uuid";

import { columnsConfig2 } from "src/app/features/ApplicationConfigurations/components/appConfigsPanelComponent/AppConfigColumnsConfig";
import type {
    AppConfigsFieldEditableItem,
    AppConfigsFormConfiguration,
    AppConfigsFormJson,
    AppConfigsTab,
    FormJson,
} from "src/app/features/ApplicationConfigurations/types/AppConfigsTypes";
import type { EditableListColumnConfig } from "../../../../components/EditableList/types/EditableListConfig";
import { EditableListRemoveStrategy } from "../../../../components/EditableList/types/EditableListRemoveStrategy";
import type { FormConfiguration, FormField, FormTab } from "../../../../components/Form/types/types";
import { deepClone } from "../../../../core/utils/utilFunctions";

export const getColumns = (): EditableListColumnConfig[] => {
    // Get EditableListAttributes by accessing field attributes in tab of form configuration in basic.json - it is always
    // accessed in 0th position
    const columnsData = { columns: columnsConfig2, removeItems: EditableListRemoveStrategy.ONLY_NEW };

    // Access data columns in the subentity, subentity does not have more than one tab, therefore fields are accessed on 0th position
    if (!columnsData) return [];

    // Update columns with corresponding sub-entity data
    const updatedColumns = columnsData.columns.map((col) => {
        return {
            ...col,
        };
    });

    return updatedColumns.map((column) => {
        return {
            ...column,
        };
    });
};

export const mapFields = (data: readonly FormField[], t: TFunction) => {
    return data.map((item) => {
        const defaultValue = { componentType: item.component, value: item.attributes?.defaultValue };
        const displayName = t(item.translationKeys?.label) || item.name;
        const validations = { componentType: item.component, validations: item.attributes?.validationRules };
        // Note: visibility default is true unless stated otherwise
        const isIncluded = item.attributes?.visible !== false;
        const { name, component, reference } = item;
        const watchedFields = item.attributes?.watchedFields;
        const condition = item.attributes?.condition;
        const readOnly = item.attributes?.readOnly;
        let urlToFetchOptions;
        if ("urlToFetchOptions" in item) {
            urlToFetchOptions = item.urlToFetchOptions;
        }
        return {
            name,
            component,
            displayName,
            isIncluded,
            validations,
            defaultValue,
            reference,
            watchedFields,
            condition,
            urlToFetchOptions,
            readOnly,
            index: `${uuidv4()}`,
            isEditing: false,
            isNew: false,
        } as AppConfigsFieldEditableItem;
    });
};

const mapTabs = (array: readonly FormTab[], t: TFunction) => {
    return array.map((childItem) => {
        const tabName = childItem.name;
        const configFields = childItem.fields ? childItem.fields : [];
        const fieldData = configFields && mapFields(configFields, t);
        return { header: tabName, key: `tab-${tabName}`, configFields: fieldData };
    });
};

// Convert Json Model to Collapse data structure
export const mapFormModelToCollapseModel = (json: FormJson, t: TFunction) => {
    if (!json) return [];
    return Object.entries(json).map((item) => {
        const formName = item[0];
        const formConfiguration = item[1];
        // if the form is a subEntity no tabs are returned. Header used to map Collapse Model to Form Model on save
        if (formConfiguration.isSubEntity) {
            return { header: formName, key: `form-${formName}`, isSubEntity: true } as AppConfigsFormConfiguration;
        }
        const tabChildren = formConfiguration.tabs || [];
        const tabData = tabChildren && mapTabs(tabChildren, t);
        return { header: formName, key: `form-${formName}`, configTabs: tabData } as AppConfigsFormConfiguration;
    });
};

export const getAttributes = (fieldParent: FormField, fieldChild: AppConfigsFieldEditableItem) => {
    let attributes = {
        defaultValue: fieldChild.defaultValue?.value,
        validationRules: fieldChild.validations?.validations,
        visible: fieldChild.isIncluded,
        watchedFields: fieldChild.watchedFields,
        condition: fieldChild.condition,
        urlToFetchOptions: fieldChild.urlToFetchOptions,
        readOnly: fieldChild.readOnly,
    };
    if (fieldParent.attributes) {
        attributes = {
            ...fieldParent.attributes,
            ...attributes,
        };
    }

    return attributes;
};

const mapChildFieldsToParentFields = (parentTab: FormTab, childTab: AppConfigsTab) => {
    if (childTab.configFields?.length) {
        childTab?.configFields.forEach((childField: AppConfigsFieldEditableItem) => {
            // Mutable type assertion used to override read-only attributes properties in FormField type
            // The origin of parentField is a clone (parentModelCopy) and can be mutated
            const parentField = parentTab.fields.find(
                (searchingField: FormField) => searchingField.name === childField.name,
            ) as Writable<FormField>;
            parentField.attributes = getAttributes(parentField, childField);
        });
    }
};

const mapChildTabsToParentTabs = (parentForm: FormConfiguration, childForm: AppConfigsFormConfiguration) => {
    if (childForm.configTabs) {
        childForm.configTabs.forEach((childTab: AppConfigsTab) => {
            // subEntity form definitions only have one tab called form.basicParameters
            if (parentForm.isSubEntity) {
                const parentTab = parentForm.tabs.find((searchingTab: FormTab) => searchingTab.name === "form.basicParameters");
                mapChildFieldsToParentFields(parentTab as FormTab, childTab);
            } else {
                const parentTab = parentForm.tabs.find((searchingTab: FormTab) => searchingTab.name === childTab.header);
                mapChildFieldsToParentFields(parentTab as FormTab, childTab);
            }
        });
    }
};

const mapChildFormsToParentForms = (parentModel: FormJson, childModel: AppConfigsFormJson) => {
    childModel?.forEach((childForm: AppConfigsFormConfiguration) => {
        const childFormName = childForm.header;
        const parentForm = parentModel[childFormName];
        mapChildTabsToParentTabs(parentForm, childForm);
    });
};

export const mapCollapseModelToFormModel = (parentModel: FormJson, childModel: AppConfigsFormJson) => {
    // clone created to allow mutation of readOnly data
    const parentModelCopy = deepClone(parentModel);
    const childModelCopy = deepClone(childModel);

    // map over child data, find the associated parent data and insert child values into parent object
    mapChildFormsToParentForms(parentModelCopy, childModelCopy);

    return parentModelCopy;
};
